"use client"

import React, { useEffect, useRef } from "react"
import { SchemaFieldList } from "@/components/shared/schema-field-list"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import { useSchemaPaths } from "@/hooks/use-schema-paths"
import { ToManyModeSelect, type UnavailableModes } from "@/components/data-pull/to-many-mode-select"
import { isColumnField } from "@/lib/ats-schema-expansion"
import { keyBelongsTo, selectedPathsFor } from "@/lib/pull-request-fields"
import { modeOf, summaryPaths, toManyRootOf, toManyRootsOf, type PullToManyMode, type PullToManyModes } from "@/lib/pull-to-many"
import type { AtsEntityTypeInfo, AtsExplorerFilter, AtsFieldDescriptor } from "@/types/ats-explorer-api"

export interface FieldSelectionSectionProps {
  /** Heading for the section (the entity's display name, or the related entity's Name) */
  title: string
  entityInfo: AtsEntityTypeInfo | null
  /** Filters applied when the vendor samples records for the schema (primary entity only) */
  filters?: AtsExplorerFilter[]
  /** The key this section's paths are stored under in a composite pull (the entity type or the related entity's name) */
  sectionKey: string
  isComposite: boolean
  /** Maps a path to the key stored in `selected` — prefixed for composite pulls */
  keyFor: (path: string) => string
  selected: Set<string>
  onChange: (next: Set<string>) => void
  /** Layout per to-many root key (`keyFor(root)`), across every section */
  toManyModes: PullToManyModes
  onToManyModeChange: (rootKey: string, mode: PullToManyMode) => void
  /** The root key laid out as rows in any section, if one is; the server allows one per pull */
  rowsRootKey: string | null
  /** The root keys given their own sheet in any section; rows cannot join them */
  sheetRootKeys: readonly string[]
  /** Called once when discovery settles: the fields on success, null when it failed */
  onSettled: (fields: AtsFieldDescriptor[] | null) => void
}

/**
 * One entity's field selection in the Data Pull wizard. Composite pulls render one
 * section per entity (primary + each related), each with its own live schema and
 * expandable association nodes; keys stored in the shared selection are prefixed per
 * entity so the API can tell them apart.
 */
export function FieldSelectionSection({ title, entityInfo, filters, sectionKey, isComposite, keyFor, selected, onChange, toManyModes, onToManyModeChange, rowsRootKey, sheetRootKeys, onSettled }: FieldSelectionSectionProps) {
  const { t } = useTranslation()
  const ownPaths = selectedPathsFor(selected, sectionKey, isComposite)
  const selectedRoots = toManyRootsOf(ownPaths)
  const schema = useSchemaPaths(entityInfo?.name ?? null, { autoLoad: true, sampleSize: 5, filters, revealPaths: ownPaths })
  const announced = useRef(false)

  // A section that keeps its key but changes entity (a preset renaming the type) settles again
  useEffect(() => {
    announced.current = false
  }, [entityInfo?.name])

  useEffect(() => {
    if (schema.fields && !announced.current) {
      announced.current = true
      onSettled(schema.error ? null : schema.fields)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema.fields])

  // A failed discovery can be retried; the next result settles the section again
  const retry = () => {
    announced.current = false
    schema.load()
  }

  const keysOf = (fields: AtsFieldDescriptor[]) => fields.map((field) => keyFor(field.path))

  // Replaces everything this section owns, including keys from expansions that are no longer on screen
  const replaceSection = (keys: string[]) => {
    const next = new Set(Array.from(selected).filter((key) => !keyBelongsTo(key, sectionKey, isComposite)))
    keys.forEach((key) => next.add(key))
    onChange(next)
  }

  const toggle = (path: string, checked: boolean) => {
    const next = new Set(selected)
    if (checked) next.add(keyFor(path))
    else next.delete(keyFor(path))
    onChange(next)
  }

  // Summary narrows the root's selection to its count and ids; the other modes lay out whatever is selected
  const chooseMode = (root: string, mode: PullToManyMode) => {
    onToManyModeChange(keyFor(root), mode)
    if (mode !== "Summary") return
    const rootKeys = new Set(ownPaths.filter((path) => toManyRootOf(path) === root).map(keyFor))
    const next = new Set(Array.from(selected).filter((key) => !rootKeys.has(key)))
    summaryPaths(root).forEach((path) => next.add(keyFor(path)))
    onChange(next)
  }

  // Rows fans the parent out per item, which no other field's rows or sheet can share
  const unavailableFor = (rootKey: string): UnavailableModes => {
    const otherRows = rowsRootKey && rowsRootKey !== rootKey ? rowsRootKey : null
    const otherSheet = sheetRootKeys.find((key) => key !== rootKey) ?? null
    return {
      Rows: otherRows ? { reason: "pull.toMany.rowsTaken", field: otherRows } : otherSheet ? { reason: "pull.toMany.rowsConflictsSheet", field: otherSheet } : undefined,
      Sheet: otherRows ? { reason: "pull.toMany.sheetConflictsRows", field: otherRows } : undefined,
    }
  }

  // The chooser sits on the list node's own row ("commissions[]") once any of its leaves is selected; the
  // to-one nodes inside its items ("commissions[].user") share the root and get none
  const nodeExtra = (field: AtsFieldDescriptor) => {
    const root = toManyRootOf(field.path)
    if (!root || field.path !== `${root}[]` || !selectedRoots.includes(root)) return null
    const rootKey = keyFor(root)
    return <ToManyModeSelect value={modeOf(toManyModes, rootKey)} onChange={(mode) => chooseMode(root, mode)} unavailable={unavailableFor(rootKey)} />
  }

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium">{title}</h3>
      {schema.error && (
        <p className="flex items-center gap-2 text-xs text-destructive">
          {schema.error}
          <button type="button" onClick={retry} disabled={schema.isLoading} className="rounded-pill border border-current px-2 py-0.5 font-semibold hover:bg-destructive/10 disabled:opacity-50">
            {t("common.actions.retry")}
          </button>
        </p>
      )}
      <SchemaFieldList
        fields={schema.fields}
        isLoading={schema.isLoading}
        selected={selected}
        onToggle={toggle}
        keyFor={keyFor}
        expandedPaths={schema.expandedPaths}
        expandingPath={schema.expandingPath}
        onExpand={(path) => void schema.expand(path)}
        onSelectDefaults={
          entityInfo?.defaultFields?.length ? () => replaceSection(entityInfo.defaultFields.map(keyFor)) : undefined
        }
        onSelectAllColumns={() => replaceSection(keysOf((schema.fields ?? []).filter(isColumnField)))}
        onClear={() => replaceSection([])}
        nodeExtra={nodeExtra}
      />
    </section>
  )
}
